import { AgGridPageRequest, InstrumentValuesReportViewModel, PortfolioReportClient } from '@dynamik/common/clients/reporting-clients';
import { CustomerService, NumericFormatterValueConverter, PortfolioReportCache, property, widget, WidgetPropertyType } from '@dynamik/common';
import { BindingEngine } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { ColumnState, GetContextMenuItemsParams, GridApi, GridOptions, SortDirection, ValueFormatterParams } from 'ag-grid-community';
import { combineLatestWith, debounceTime, delayWhen, ReplaySubject, switchMap, takeUntil, tap } from '../../../rxjs-local';
import { getExcelStyles } from '../../../resources/agGrid/excel-styles';
import { ThemeService } from '../../../services/theme-service';
import { AgGridSortModel } from '@dynamik/common/clients/transaction-clients';

export enum TopBottomInstrumentsWidgetStartFrom {
  ToDateYearStart = 1,
  FromDate = 2
}

declare module '@dynamik/common/clients/reporting-clients' {
  interface IInstrumentValuesReportViewModel {
    estimatedValuePercent?: number;
  }
  interface InstrumentValuesReportViewModel extends IInstrumentValuesReportViewModel {
    estimatedValuePercent?: number;
  }
}

@customElement('top-bottom-instruments-widget')
@widget('Top/Bottom instruments', false, true, 'Description')
export class TopBottomInstrumentsWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportClient: PortfolioReportClient, bindingEngine: BindingEngine,
    private numericFormatter: NumericFormatterValueConverter, public themeService: ThemeService) {
    super(element, bindingEngine, customerService);
  }

  fieldLabel: string;
  total: number;
  gridApi: GridApi<InstrumentValuesReportViewModel>;
  gridReady$ = new ReplaySubject<void>(1);
  gridOptions: GridOptions<InstrumentValuesReportViewModel>;
  moneyFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, this.precision);
  TopBottomInstrumentsWidgetStartFrom = TopBottomInstrumentsWidgetStartFrom;

  @property(null)
  columnStates?: ColumnState[];

  @property(null)
  filterModel?: ReturnType<GridApi['getFilterModel']>;

  @property('Data', undefined, WidgetPropertyType.List, [
    { value: TopBottomInstrumentsWidgetStartFrom.ToDateYearStart, label: '"To" date year start' },
    { value: TopBottomInstrumentsWidgetStartFrom.FromDate, label: '"From" date' }
  ])
  startFrom: TopBottomInstrumentsWidgetStartFrom = TopBottomInstrumentsWidgetStartFrom.ToDateYearStart;

  @property('Data')
  count: number = 10;

  @property('Data')
  precision?: number = 2;

  @property('Table')
  fontSize?: number = 14;

  @property('Table')
  rowHeight?: number;


  override activate(config: unknown) {
    super.activate(config)
    this.gridOptions = {
      defaultColDef: {
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
      },
      autoGroupColumnDef: {
        valueGetter: params => params.data.name
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
      popupParent: document.body,
      rowHeight: this.rowHeight,
      excelStyles: getExcelStyles(9),
      rowModelType: 'clientSide',
      rowSelection: { mode: 'singleRow', checkboxes: false },
      groupTotalRow: 'bottom',
      suppressAggFuncInHeader: true,
      groupHideOpenParents: false,
      animateRows: false,
      debug: false,
      scrollbarWidth: 11,
      onColumnMoved: () => this.saveColumnState(),
      onColumnVisible: () => this.saveColumnState(),
      onColumnResized: () => this.saveColumnState(),
      onFilterChanged: () => this.saveColumnState(),
      onSortChanged: () => this.saveColumnState(),
      onDisplayedColumnsChanged: params => {
        if (params.source !== 'gridInitializing') {
          this.saveColumnState();
        }
      },
      getRowId: x => x.data.instrumentId.toString(),
      onGridReady: params => {
        this.gridApi = params.api;
        this.suppressSaveColumnState = true;
        setTimeout(() => this.suppressSaveColumnState = false, 2000);
        if (this.columnStates?.length) {
          this.gridApi.applyColumnState({ state: this.columnStates, applyOrder: true });
        }
        if (this.filterModel?.length) {
          this.gridApi.setFilterModel(this.filterModel);
        }
        this.gridReady$.next();
      }
    };
  }

  override subscribeToFilter() {
    this.customerService.customerAndDates$
      .pipe(
        delayWhen(() => this.gridReady$),
        combineLatestWith(this.refresh$),
        debounceTime(500),
        tap(() => {
          this.error = undefined;
          this.gridApi.setGridOption('loading', true);
          this.notifyLoading();
        }),
        switchMap(async ([filter]) => {
          const dateFromStr = this.startFrom === TopBottomInstrumentsWidgetStartFrom.FromDate ? filter.dates.fromStr : undefined;
          return this.portfolioReportClient.instrumentValuesGrid(filter.customer.companyId, dateFromStr, filter.dates.toStr, undefined, undefined, 0,
            new AgGridPageRequest({
              startRow: 0,
              endRow: this.count,
              sortModel: this.gridApi.getColumnState().filter(x => x.sort != null).map(x => new AgGridSortModel({ colId: x.colId, sort: x.sort as SortDirection })),
            })
          ).catch(e => e as Error);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(x => {
        this.gridApi.setGridOption('loading', false)
        if (x instanceof Error) {
          this.error = x;
        } else {
          x.values.forEach(y => {
            y.estimatedValuePercent = y.estimatedValueClosingBalance / x.total * 100;
            y.profitLossChangeYearToDateInPercent *= 100;
            y.profitLossChangePeriodInPercent *= 100;
          });
          this.gridApi.setGridOption('rowData', x.values);
          this.updateTotal();
        }
        this.notifyLoaded();
      });
  }

  updateTotal() {
    var total = new InstrumentValuesReportViewModel({
      name: 'TOTAL', estimatedValueClosingBalance: 0, costPriceClosingBalance: 0, unrealizedProfitLossEndOfLastYear: 0,
      unrealizedProfitLossClosingBalance: 0, unrealizedProfitLossChangeYearToDate: 0, realizedProfitLossChangeYearToDate: 0,
      dividendChangeYearToDate: 0, interestChangeYearToDate: 0, profitLossChangeYearToDate: 0, estimatedValueChangeYearToDate: 0,
      instrumentId: -1, estimatedValuePercent: 0
    });
    this.gridApi.forEachNodeAfterFilterAndSort((node, index) => {
      if (index >= this.gridApi.paginationGetPageSize()) {
        return;
      }
      const c = node.data;
      total.estimatedValueClosingBalance += c.estimatedValueClosingBalance;
      total.estimatedValuePercent += c.estimatedValuePercent ?? 0;
      total.costPriceClosingBalance += c.costPriceClosingBalance;
      total.unrealizedProfitLossClosingBalance += c.unrealizedProfitLossClosingBalance;
      total.unrealizedProfitLossEndOfLastYear += c.unrealizedProfitLossEndOfLastYear;
      total.unrealizedProfitLossChangeYearToDate += c.unrealizedProfitLossChangeYearToDate;
      total.realizedProfitLossChangeYearToDate += c.realizedProfitLossChangeYearToDate;
      total.dividendChangeYearToDate += c.dividendChangeYearToDate;
      total.interestChangeYearToDate += c.interestChangeYearToDate;
      total.profitLossChangeYearToDate += c.profitLossChangeYearToDate;
    });
    this.gridApi.setGridOption('pinnedBottomRowData', [total]);
  }

  override configChanged(key: keyof TopBottomInstrumentsWidget) {
    switch (key) {
      case 'columnStates':
      case 'filterModel':
        this.refresh$.next();
        break;
      case 'rowHeight':
        this.rowHeightChanged();
        break;
      case 'count':
        this.gridApi.setGridOption('paginationPageSize', this.count);
        this.updateTotal();
        break;
      case 'precision':
        this.refresh$.next();
        break;
      default:
        this.refresh$.next(undefined);
        break;
    }
  }

  getContextMenuItems(params: GetContextMenuItemsParams<InstrumentValuesReportViewModel>) {
    return [
      'autoSizeAll',
      'resetColumns',
      { name: 'Fit All Columns', action: () => this.gridApi.sizeColumnsToFit() },
      'separator',
      {
        name: 'Export to Csv',
        action: async () => params.api.exportDataAsCsv({ columnSeparator: ';' }),
      },
      {
        name: 'Export to Excel',
        action: async () => params.api.exportDataAsExcel({
          skipPinnedTop: false,
          skipPinnedBottom: false,
          allColumns: false,
          onlySelected: false,
          fileName: 'PortfolioOverview',
          sheetName: 'PortfolioOverview'
        }),
      }
    ];
  }

  suppressSaveColumnState: boolean;
  saveColumnState() {
    if (this.suppressSaveColumnState) {
      return;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.filterModel = this.gridApi.getFilterModel();
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  rowHeightChanged() {
    this.gridOptions.rowHeight = this.rowHeight;
    this.gridApi.resetRowHeights();
  }

}
